/**
 * 行排版逻辑 —— 纯函数，零 Vue / 零图引擎依赖。
 *
 * 负责：
 *   1. 按分组行的正则，把属性行归入分组（resolveSections）
 *   2. 把行序列编译成可顺序渲染的指令列表（buildRenderList）
 *   3. 列出折叠分组需要保留锚点的端口（projectGroupSockets）
 *   4. 变参行的实例数量 / 端口 id / 序号解析（repeater*）
 *
 * 抽成纯函数的原因和 connectionRules 一样：能脱离 UI 直接测（见
 * scripts/verify-node-rows.ts），并且以后接画布视图时可以原样复用。
 */
import type { GroupRow, NodeRow, RepeaterRow, SocketDef } from '../types/node'
import type { SocketType } from '../types/socket'

/** 常规行高 px —— 必须与 theme.css 的 --np-row-h 默认值一致 */
export const DEFAULT_ROW_HEIGHT = 22
/** 展示行默认高度 px */
export const DEFAULT_DISPLAY_ROW_HEIGHT = 80

/* ==========================================================================
   1. 分组解析
   ========================================================================== */

export interface ResolvedSection {
  /** null = 未归入任何分组的散行 */
  group: GroupRow | null
  rows: NodeRow[]
}

function compileMatch(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern)
  } catch {
    // 正则写错不应该让整个节点崩掉，退化为「不匹配任何行」
    return null
  }
}

/**
 * 按分组行的 `match` 正则把其他行归入分组。
 *
 * 规则：
 *   · 每个非分组行只归入**第一个**匹配到它的分组（分组声明顺序即优先级）
 *   · 已归组的行从原位置移走，改在分组行下方渲染
 *   · 未匹配任何分组的行保持原位
 *   · 分组声明之间不会嵌套（分组行不参与匹配）
 */
export function resolveSections(rows: readonly NodeRow[]): ResolvedSection[] {
  const groups = rows.filter((r): r is GroupRow => r.kind === 'group')

  if (groups.length === 0) {
    return rows.map((row) => ({ group: null, rows: [row] }))
  }

  const matchers = groups.map((group) => ({
    group,
    regex: compileMatch(group.match),
  }))

  const ownerOf = new Map<string, string>()
  const membersOf = new Map<string, NodeRow[]>()
  for (const group of groups) membersOf.set(group.id, [])

  for (const row of rows) {
    if (row.kind === 'group') continue
    const hit = matchers.find((m) => m.regex?.test(row.id))
    if (!hit) continue
    ownerOf.set(row.id, hit.group.id)
    membersOf.get(hit.group.id)?.push(row)
  }

  const sections: ResolvedSection[] = []
  for (const row of rows) {
    if (row.kind === 'group') {
      sections.push({ group: row, rows: membersOf.get(row.id) ?? [] })
      continue
    }
    if (ownerOf.has(row.id)) continue
    sections.push({ group: null, rows: [row] })
  }
  return sections
}

/* ==========================================================================
   2. 渲染指令列表
   ========================================================================== */

export type RenderItem =
  | { kind: 'row'; key: string; row: Exclude<NodeRow, RepeaterRow>; inGroup: boolean }
  | {
      kind: 'repeater-row'
      key: string
      row: RepeaterRow
      inGroup: boolean
      /** 实例序号，0 起 */
      index: number
      /** 当前渲染的实例总数 */
      total: number
      /** 该实例的端口（id 由 handlePattern 生成） */
      socket: SocketDef
    }
  | {
      kind: 'group'
      key: string
      group: GroupRow
      memberCount: number
      collapsed: boolean
      /** 折叠时需要在分组行上保留锚点的端口（展开时为空） */
      anchors: ProjectedSocket[]
    }

export interface BuildOptions {
  /**
   * 隐藏未连接的端口（Blender 的 Ctrl+H）。
   *
   * 只藏 input / output 行 —— 变参行是「增删端口」的容器，藏掉就没地方接线了。
   * 在分组解析**之前**过滤，这样折叠分组的锚点、成员计数都会自动跟着变。
   */
  hideUnused?: boolean
}

/**
 * 这一行会不会因为「隐藏未连接端口」而被藏掉。
 *
 * 抽出来是因为**节点上要显示藏了几个**（`hiddenPortCount`），
 * 两处必须用同一个判定 —— 各写一遍迟早会不一致，
 * 表现就是「标记说藏了 3 个，实际只少了 2 行」。
 *
 * ⚠️ 读的是 `socket.connected`，那是宿主**从连线推导出来**的投影结果，
 * 不是用户填的值 —— 视图状态当真相用会出事，这里只是消费它。
 */
export function isUnusedPortRow(row: NodeRow): boolean {
  if (row.kind !== 'input' && row.kind !== 'output') return false
  return !row.socket.connected
}

/** 开「隐藏未连接端口」后会藏起来几个端口（给节点上的标记用） */
export function hiddenPortCount(rows: readonly NodeRow[]): number {
  return rows.filter(isUnusedPortRow).length
}

/**
 * 这一行**在节点上不画**（但它在 `rows` 里，值是文档数据）。
 *
 * 今天只有一种：`panelOnly` 的属性行 —— JS 节点的 `ports` / `code`。
 * 它们是"特殊设置"，编辑在「属性」面板里做，节点上只留输入输出端口。
 *
 * ⚠️ 独立成一个函数是为了**两处共用**：渲染列表要跳过它，
 * 而将来若有别的地方按行算高度 / 数行，也必须跳过它。
 */
export function isPanelOnlyRow(row: NodeRow): boolean {
  return row.kind === 'property' && Boolean(row.panelOnly)
}

export function buildRenderList(
  rows: readonly NodeRow[],
  options: BuildOptions = {},
): RenderItem[] {
  const items: RenderItem[] = []

  /*
   * ⚠️ `panelOnly` 的过滤放在**最前面**（和 `hideUnused` 一起）：
   * 它连"分组成员"都不该算 —— 混进去会让折叠分组的成员计数虚高。
   */
  let source = rows.filter((row) => !isPanelOnlyRow(row))
  if (options.hideUnused) source = source.filter((row) => !isUnusedPortRow(row))

  for (const section of resolveSections(source)) {
    if (!section.group) {
      pushRows(items, section.rows, false)
      continue
    }

    const group = section.group
    /*
     * ⚠️ 三级取默认：用户翻过（`collapsed` 有值）→ 用它；
     * 否则看声明里的 `expanded`；**都没有 = 收起**（见 `GroupRow.expanded`）。
     */
    const collapsed = group.collapsed ?? !group.expanded

    items.push({
      kind: 'group',
      key: `g:${group.id}`,
      group,
      memberCount: section.rows.length,
      collapsed,
      anchors: collapsed ? projectGroupSockets(section.rows) : [],
    })

    if (!collapsed) pushRows(items, section.rows, true)
  }

  return items
}

function pushRows(
  items: RenderItem[],
  rows: readonly NodeRow[],
  inGroup: boolean,
): void {
  for (const row of rows) {
    if (row.kind === 'repeater') {
      const side = repeaterSide(row)

      const total = repeaterCount(row)
      for (let index = 0; index < total; index += 1) {
        items.push({
          kind: 'repeater-row',
          key: `rep:${row.id}:${index}`,
          row,
          inGroup,
          index,
          total,
          socket: repeaterInstanceSocket(row, index),
        })
      }
      continue
    }
    items.push({
      kind: 'row',
      key: `r:${row.id}`,
      row,
      inGroup,
    })
  }
}

/* ==========================================================================
   3. 折叠分组的端口锚点
   ========================================================================== */

export interface ProjectedSocket {
  socket: SocketDef
  side: 'input' | 'output'
}

/**
 * 折叠分组需要保留锚点的端口。
 *
 * 只有**已连接**的端口需要锚点 —— 未连接的端口没有线，折叠后消失
 * 不会造成困惑。
 *
 * 位置不在这里算：所有锚点都落在分组行的**同一个位置**（垂直居中），
 * 不做错开。一排点既乱又容易被误当成可拖拽的端口；单点锚点让
 * 「这一组有几条线进出」变成一个干净的视觉信号。
 */
export function projectGroupSockets(
  rows: readonly NodeRow[],
): ProjectedSocket[] {
  const anchors: ProjectedSocket[] = []

  for (const row of rows) {
    if (row.kind === 'input' || row.kind === 'output') {
      if (row.socket.connected) {
        anchors.push({
          socket: row.socket,
          side: row.kind === 'input' ? 'input' : 'output',
        })
      }
      continue
    }

    if (row.kind === 'repeater') {
      const connected = row.connected ?? []
      for (let i = 0; i < connected.length; i += 1) {
        if (!connected[i]) continue
        anchors.push({
          socket: repeaterInstanceSocket(row, i),
          side: repeaterSide(row),
        })
      }
    }
  }

  return anchors
}

/**
 * **节点整体折叠**时需要保留锚点的端口。
 *
 * 折叠后 body 整个不渲染，所有行级 Handle 都会消失 ——
 * 如果不补锚点，挂在这个节点上的线就失去了落点。
 *
 * 规则与折叠分组完全一致（本来就是同一件事）：
 *   · 只有已连接的端口需要锚点
 *   · 输入挂左缘、输出挂右缘
 *   · 全部落在同一个位置（节点垂直居中），不做上下错开
 */
export function projectNodeSockets(
  rows: readonly NodeRow[],
): ProjectedSocket[] {
  return projectGroupSockets(rows)
}

/* ==========================================================================
   4. 变参行（repeater）
   ========================================================================== */

/** 实例端口的 handle id 模板，默认 `${id}.{i}` */
function patternOf(row: RepeaterRow): string {
  return row.handlePattern ?? `${row.id}.{i}`
}

/** 第 index 个实例的 handle id */
export function repeaterHandle(row: RepeaterRow, index: number): string {
  return patternOf(row).replace('{i}', String(index))
}

/**
 * **按行 id 反解句柄序号**（拿不到行对象时用；不是这一行的 → `null`）。
 *
 * ⚠️ 内核里必须有一个实现：这是**节点层**的句柄规则（`${rowId}.{i}`），
 * 而包那边也要用（页面的组件层序 `items.N`、文档的页序 `pages.N`）——
 * 两边各写一次的下场是"一处改了、另一处静默失效"：层序读出来永远是
 * `null`、所有项并列，看起来只像排序不对。包可以调它；
 * 内核**不许**反过来知道包里的行 id（那正是静态边界那条断言在管的）。
 */
export function repeaterIndexFromHandle(handle: string | null | undefined, rowId: string): number | null {
  const prefix = `${rowId}.`
  if (!handle || !handle.startsWith(prefix)) return null
  const index = Number(handle.slice(prefix.length))
  return Number.isInteger(index) && index >= 0 ? index : null
}


/** 第 index 个实例的端口定义 */
/**
 * 某个实例的类型。
 *
 * 按实例覆盖优先（见 `RepeaterRow.instanceTypes`），否则回退到行声明的。
 * 组 / 循环的输入靠它做到「没连时通配、连上后确认成上游类型」。
 */
export function repeaterInstanceType(row: RepeaterRow, index: number): SocketType {
  return row.instanceTypes?.[index] ?? row.socketType
}

export function repeaterInstanceSocket(
  row: RepeaterRow,
  index: number,
): SocketDef {
  return {
    id: repeaterHandle(row, index),
    type: repeaterInstanceType(row, index),
    /* ⚠️ 精确连接是**行上的声明**，透传到每个实例端口（见 `RepeaterRow.strict`） */
    ...(row.strict ? { strict: true } : {}),
    connected: Boolean(row.connected?.[index]),
  }
}

/**
 * 变参行的实例**连线** —— 按方向取对应那一侧。
 *
 * ★ 方向的分界线就在这一个函数里：
 *   · `'input'`  —— 这个节点是边的 **target**（值从上游喂进来）
 *   · `'output'` —— 这个节点是边的 **source**（值从这里出去）
 *
 * ⚠️ 抽出来是为了**只有一份**：投影（算连接态/标签/类型）、连线菜单
 * （列出可接的实例）两处都要按方向找边；各写一份的话，两处迟早对不上，
 * 而症状是「画面上连着的线，在候选菜单里找不到」这种很难查的东西。
 */
export function repeaterEdges<E extends RepeaterEdgeLike>(
  row: RepeaterRow,
  nodeId: string,
  edges: readonly E[],
): { index: number; edge: E }[] {
  const side = repeaterSide(row)
  const found: { index: number; edge: E }[] = []
  for (const edge of edges) {
    const owner = side === 'input' ? edge.target : edge.source
    if (owner !== nodeId) continue
    const handle = side === 'input' ? edge.targetHandle : edge.sourceHandle
    const index = repeaterIndexOf(row, handle ?? '')
    if (index >= 0) found.push({ index, edge })
  }
  return found
}

/** `repeaterEdges` 只要求这几个字段（图层的边比它多几个也行） */
export interface RepeaterEdgeLike {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}

/**
 * 这一行是输入还是输出。
 *
 * ⚠️ 老数据没有 `side` 字段 → 一律 `'input'`（那时只有输入方向）。
 */
export function repeaterSide(row: RepeaterRow): 'input' | 'output' {
  return row.side ?? 'input'
}

/** 是不是输出方向的变参行 */
export function isOutputRepeater(row: RepeaterRow): boolean {
  return repeaterSide(row) === 'output'
}

/**
 * 当前应该渲染几个实例。
 *
 * 规则：**最后一个已连接的实例之后，永远保留一个空行**，供下一次连线。
 * 所以用户连上最后一个空行时，列表会自动长出一行 —— 不用手动点「+」。
 *
 * `connected` 的长度不直接决定实例数：中间留空的实例不会撑出多余行。
 */
export function repeaterCount(row: RepeaterRow): number {
  const connected = row.connected ?? []
  let lastConnected = -1
  for (let i = 0; i < connected.length; i += 1) {
    if (connected[i]) lastConnected = i
  }
  return repeaterCountFor(row, lastConnected)
}

/**
 * 同上，但直接给「最后一个已连接实例的序号」。
 *
 * 单独暴露是为了让「由边推导连接态」的调用方复用同一套钳制逻辑 ——
 * 否则两处各写一遍 min/max 处理，迟早会不一致。
 * `lastConnected = -1` 表示一个都没连。
 */
export function repeaterCountFor(
  row: RepeaterRow,
  lastConnected: number,
): number {
  /*
   * ⚠️ 输出方向：**不留空槽**（见 `RepeaterRow.side` 的注释）。
   *
   * 数量 = max(显式 count ?? 0, 内部声明的 min ?? 0, 已连接数)，
   * 一个都不连、也没显式声明 → **0 个端口**（不是 1，更不是 2）。
   * 留个空槽给谁连？输出的值由节点自己算出来，没有「补一个槽」这回事。
   */
  if (isOutputRepeater(row)) {
    /*
     * ★ 输出方向的数量**只由声明决定**，和「连了几路」无关。
     *
     * 之前把 `lastConnected + 1` 也算进去了，后果是：一条指向 `out9` 的
     * 陈旧边就能让一个声明 2 路的输出长出 9 路 —— 而输出是节点
     * **声明**的，没有「连出来的实例」这回事。
     */
    const max = row.max ?? Number.POSITIVE_INFINITY
    /*
     * ⚠️ `freeSlot` —— **末尾额外留一个空槽**，给「接一根长一路」当入口。
     *
     * 只有**组的接口节点**用它（见 `groupInputRows` / `groupOutputRows`）：
     * 那些节点的件数是**靠接线长**的，没有空槽就是个死锁
     * （0 个端口 → 没地方接 → 永远 0 个）。
     *
     * ⚠️ **不能无条件 `+1`**：普通节点的输出变参行不该多长一个槽
     * （试过，`verify:render` 当场红：声明 2 路的输出冒出 `out2`）。
     * 而且要显式声明，不能靠 `min` 猜 —— 「min = 声明数」和
     * 「min = 声明数 + 空槽」在数据上分不开，猜错就会让外层凭空多端口。
     */
    if (!row.freeSlot) {
      return Math.min(max, Math.max(row.count ?? 0, row.min ?? 0))
    }
    /*
     * ★★ `freeSlot` 的行**跟着连线长**（不是「固定多一个」）。
     *
     * ⚠️ 这一条是**实测**踩出来的，而且是「一半」的第二次：
     * 第一版写成 `声明数 + 1`（固定额度），于是
     *
     * ```text
     *   接上 in.0 之后 → 渲染数还是 1 → 那个空槽**还是 in.0**（已经被占了）
     *                  → 用户看到「接完线没有多出空的端口」
     * ```
     *
     * 而**外层却对了**（`groupInterfaceOf` 从边推，会长）——
     * 于是症状变成「外面正常、里面不长」，更难看出是同一处的问题。
     *
     * 现在：`max(连线数, 声明数) + 1` —— 接一根，空槽往后挪一格。
     * ⚠️ **只对 `freeSlot` 的行生效**：普通输出变参行仍然只认声明，
     * 「陈旧边把声明 2 路撑成 9 路」那个 bug 不能回来（没有 `freeSlot`
     * 就没有空槽，越界仍然由 `repeaterIndexOf` 挡）。
     */
    return Math.min(max, Math.max(lastConnected + 1, row.count ?? 0, row.min ?? 0) + 1)
  }

  const min = Math.max(1, row.min ?? 1)
  const max = row.max ?? Number.POSITIVE_INFINITY

  /*
   * 显式 count：用户点「＋」摆出来的空行。
   *
   * 空行推不出来（没有边），所以必须能显式声明。但仍然要容纳
   * 「比 count 更靠后的实例被连上了」—— 这时以边为准（max），
   * 否则接在第 5 项上的线会因为 count=2 而看不见。
   */
  if (row.count !== undefined) {
    return Math.min(max, Math.max(min, row.count, lastConnected + 1))
  }

  return Math.max(min, Math.min(max, lastConnected + 2))
}

/**
 * **实际用了几个**实例（最后一个已连接 + 1）。
 *
 * ⚠️ 和 `repeaterCount` 不是一回事：
 *   `repeaterCount`      = 渲染几个（末尾**多留一个空槽**好继续连）
 *   `wiredRepeaterCount` = 实际连了几个
 *
 * 同步接口（外部连了几个 → 内部生成几行）必须用后者，
 * 用前者会**永远多出一行** —— 外面连 2 个，里面长出 3 行。
 */
/**
 * **实际连了几个**，一个都没连就是 **0**。
 *
 * ⚠️ 和 `wiredRepeaterCount` 的区别只有一处，但很关键：
 * 那个会钳到 `min`（默认 1）—— 那是**渲染**需要的（末尾留个槽好接线），
 * 不是接口的真实数量。
 *
 * 用它来推导接口：「外面一个都没连 → 里面一行都不该有」。
 * 用 `wiredRepeaterCount` 的话，一个都没连也会长出 1 行 —— 真实反馈过。
 */
export function connectedRepeaterCount(row: RepeaterRow): number {
  const connected = row.connected ?? []
  let last = -1
  for (let i = 0; i < connected.length; i += 1) {
    if (connected[i]) last = i
  }
  return last + 1
}

/**
 * **下一个空位的序号**（要接新线时该报哪一个实例）。
 *
 * = 「最后一个已连实例」的下一个 = `connectedRepeaterCount(row)`。
 * 没连任何东西时是 **0**（第 1 项）。
 *
 * ⚠️⚠️ 这条抽出来是因为**踩过一次 off-by-one，而且症状会误导人**：
 *
 * ```text
 *   拖线松手落空 → 菜单里写「新建调试输出（监听 第 2 项）」
 *   建出来的节点：第 1 项空着，线接在第 2 项上
 *   再拖一下节点 → 界面"塌"成第 1 项（用户的原话：「拖拽一下节点就塌陷链接到监听1」）
 * ```
 *
 * 原来那句是 `const index = repeaterCount(row)`，注释还写着
 * 「`repeaterCount` 已经含末尾那个空槽，所以它的下标正好是下一个」——
 * **这句话是错的**：`repeaterCount` 是**件数**（渲染几行），
 * 空槽的下标是 `件数 - 1`，下一个空位是 `lastConnected + 1`。
 * 一个都没连时 `repeaterCount` 是 1（那一行就是第 1 项留给你的空槽），
 * 于是"下一个空位"被算成了 1 → 跳过第 1 项。
 *
 * ⚠️ 顺带说明为什么它"看起来有时是对的"：连了 1 个实例时
 * `repeaterCount` 恰好是 2、`lastConnected + 1` 也是 1 —— **只有
 * 「一个都没连」和「中间有空档」这两种情况会露馅**，而新节点恰好是前者。
 */
export function nextRepeaterIndex(row: RepeaterRow): number {
  return connectedRepeaterCount(row)
}

export function wiredRepeaterCount(row: RepeaterRow): number {
  const connected = row.connected ?? []
  let last = -1
  for (let i = 0; i < connected.length; i += 1) {
    if (connected[i]) last = i
  }
  return Math.max(Math.max(1, row.min ?? 1), last + 1)
}

/**
 * 从 handle id 反解实例序号；不是本变参行的端口则返回 -1。
 *
 * 用前缀/后缀匹配而不是逐个试，所以不受实例数上限影响。
 */
export function repeaterIndexOf(row: RepeaterRow, handleId: string): number {
  const [prefix, suffix = ''] = patternOf(row).split('{i}')
  if (!prefix || !handleId.startsWith(prefix) || !handleId.endsWith(suffix)) {
    return -1
  }
  const middle = handleId.slice(prefix.length, handleId.length - suffix.length)
  if (!/^\d+$/.test(middle)) return -1
  const index = Number.parseInt(middle, 10)

  /*
   * ★ **输出方向要有界**：`out9` 在一个只声明了 2 路的节点上**不是端口**。
   *
   * 输入方向刻意**不设界** —— 那个越界的下标正是「连到末尾空槽、
   * 于是多出一路」的生长机制。输出方向没有空槽，所以越界就是错的
   * （手改的文档、导入的包都可能带着这种边）。
   *
   * ⚠️ 判据只看**声明**（`min` / `count`），不看 `connected`：
   * 看连线的话会和 `repeaterCountFor` 互相递归，而且正是上面
   * 「陈旧边撑大输出」那个 bug 的来源。
   */
  if (isOutputRepeater(row)) {
    /*
     * ★★ **末尾那个空槽要放行** —— 它是「接一根长一路」的入口，
     * 拒掉它就等于把接口节点焊死了。
     *
     * ⚠️ 这一条是**实测**踩出来的：上一版用「`index >= 声明数` 就拒」，
     * 于是新组的内部输入节点 `min = 0` → `in.0`（那个**渲染出来**的空槽）
     * 被 `findSocketIn` 判成「不是端口」→ 线接不上 ——
     * 用户报的正是「**输入节点的输出端口不能接任何端口**」。
     *
     * ⚠️⚠️ 边界**必须自己算，不能借 `repeaterCountFor(row, -1)`** ——
     * 那会变成一个**循环依赖**，而且症状很隐蔽：
     *
     * ```text
     *   ① 投影：repeaterCountFor(row, lastConnected=0)
     *            → freeSlot 是 +1 → total = 2 → 写回 connected=[true,false]
     *   ② 判定：repeaterCountFor(row, -1)      ← 传的是「什么都没连」
     *            → total = 1 → 只允许 in.0
     *   ③ 于是 in.1（**画面上明明有一个空槽**）被拒 → 「新增的不能链接」
     * ```
     *
     * 而 `repeaterEdges` 又要靠 `repeaterIndexOf` 才能数出连线 ——
     * 三者互相喂，永远差一格。所以这里改成**只看声明 + 已连了几路**：
     *
     * ```text
     *   末尾可接的下标 = max(声明数, 已连路数) + (留空槽 ? 1 : 0)
     * ```
     *
     * 它和渲染端**同源**（都用「声明 + 连线」），只是各自钳自己的上限。
     */
    const declared = Math.max(row.count ?? 0, row.min ?? 0)
    const wired = (row.connected ?? []).filter(Boolean).length
    const limit = Math.max(declared, wired) + (row.freeSlot ? 1 : 0)
    if (index >= limit) return -1
  }
  return index
}

/**
 * 变参行「压紧」：已连接的实例序号 → 它应该去的新序号。
 *
 * 不变式：**已连接的实例必须占据 0..k-1，中间不许留空洞。**
 *
 * 为什么这是硬要求而不是洁癖：实例数量是由
 * `最后一个已连实例的序号 + 2` 推导的（见 repeaterCountFor）。
 * 一旦中间有空洞，`lastConnected` 就会被顶大 ——
 * 列表凭空多出一行，而前面那行是空的。
 *
 * 典型触发场景：把 args.0 的线拖到 args.1 上松手
 *   → 连线落在 1，0 空出来 → lastConnected 从 0 变 1 → 行数 +1，首行为空。
 *
 * @param occupied 已连接的实例序号（不要求有序）
 * @returns 旧序号 → 新序号；没有空洞时就是恒等映射
 */
export function compactIndexMap(occupied: readonly number[]): Map<number, number> {
  const map = new Map<number, number>()
  const sorted = [...occupied].sort((a, b) => a - b)
  sorted.forEach((oldIndex, newIndex) => map.set(oldIndex, newIndex))
  return map
}

/**
 * 变参行拖拽排序的置换：把 `from` 位置的实例移到 `to` 位置。
 *
 * 返回 **旧序号 → 新位置** 的映射 —— 调用方据此改写挂在这些实例上的连线
 * 的 targetHandle。
 *
 * 之所以做「移动」而不是「两两交换」：把第 5 个拖到第 1 个时，
 * 交换要拖 4 次，移动只要 1 次。
 */
export function moveIndexMap(
  total: number,
  from: number,
  to: number,
): Map<number, number> {
  const order = Array.from({ length: Math.max(0, total) }, (_, i) => i)
  const map = new Map<number, number>()

  if (total <= 0 || from < 0 || to < 0 || from >= total || to >= total) {
    // 越界 → 恒等映射，调用方不需要特判
    order.forEach((oldIndex) => map.set(oldIndex, oldIndex))
    return map
  }

  const [moved] = order.splice(from, 1)
  order.splice(to, 0, moved as number)
  order.forEach((oldIndex, newIndex) => map.set(oldIndex, newIndex))
  return map
}

/**
 * 变参行的元素类型 —— 所有实例必须同类型，所以直接取声明值。
 * 之所以单独成函数：将来若要支持「元素类型由连线推导」，改这里即可。
 */
export function repeaterItemType(row: RepeaterRow): SocketTypeLike {
  return row.socketType
}

/** 避免直接依赖 socket 模块的类型名，保持本文件零外部类型耦合 */
type SocketTypeLike = RepeaterRow['socketType']

/**
 * **深拷贝一组行**。
 *
 * 节点类型定义里的行是**模板**（数据），每个实例都必须拿到自己的一份 ——
 * 否则「改一个节点的控件值」会连带改掉画布上**所有**同类型节点。
 * 这个坑踩过两次（常量节点共享了行里的 `widget` 对象，
 * 三个数值常量全变成最后一个的值，列表收出 `[4,4,4]`）。
 *
 * ⚠️ 用 JSON 而不是手写逐层拷贝：行的数据**本来就必须是 JSON 安全的** ——
 * 文档格式（`graph/serialization.ts`）就是靠 `JSON.parse(JSON.stringify())`
 * 存取它们的。既然文档能往返，这里就一定能往返。
 * 手写逐层拷贝反而容易漏掉一层（`widget.options` / `row.connected`）。
 */
export function cloneRows<T extends NodeRow>(rows: readonly T[]): T[] {
  return JSON.parse(JSON.stringify(rows)) as T[]
}
